import { Button, InputText, Menu } from "@/deps/ui-library";
import { formatDate } from "@/lib/dates";
import type { DateShortcut } from "./glPeriods";

/**
 * One field for a range: From, To and the shortcuts on its arrow. A shortcut only fills the dates, and
 * the field never shows its name, so what the field says is always what the report will read.
 */
export function DateRangeField({
  from,
  to,
  shortcuts,
  onChange,
  onEnter,
}: {
  from: string;
  to: string;
  shortcuts: DateShortcut[];
  onChange: (range: { from?: string; to?: string }) => void;
  onEnter?: () => void;
}) {
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") onEnter?.();
  };
  return (
    <div className="border-input-border bg-input-background flex h-8 items-center gap-1 rounded-md border pl-1">
      <InputText.Input
        type="date"
        aria-label="From"
        config={{ size: "sm", variant: "ghost" }}
        className="h-full w-[136px] bg-transparent text-sm"
        value={from}
        onChange={(event) => {
          if (event.target.value) onChange({ from: event.target.value });
        }}
        onKeyDown={onKeyDown}
      />
      <span className="text-muted-foreground text-xs">–</span>
      <InputText.Input
        type="date"
        aria-label="To"
        config={{ size: "sm", variant: "ghost" }}
        className="h-full w-[136px] bg-transparent text-sm"
        value={to}
        onChange={(event) => {
          if (event.target.value) onChange({ to: event.target.value });
        }}
        onKeyDown={onKeyDown}
      />
      <Menu.Root positioning={{ placement: "bottom-end", gutter: 6 }}>
        <Menu.Trigger asChild>
          <Button.IconButton
            icon="ChevronDown"
            variant="ghost"
            colorScheme="default"
            size="xs"
            aria-label="Date shortcuts"
          />
        </Menu.Trigger>
        <Menu.Content>
          {shortcuts.map((shortcut) => (
            <Menu.Item
              key={shortcut.id}
              value={shortcut.id}
              onClick={() => onChange({ from: shortcut.from, to: shortcut.to })}
            >
              <span className="flex w-full items-center justify-between gap-4">
                <span className={shortcut.from === from && shortcut.to === to ? "font-medium" : ""}>
                  {shortcut.label}
                </span>
                <span className="text-muted-foreground text-xs whitespace-nowrap">
                  {formatDate(shortcut.from)} – {formatDate(shortcut.to)}
                </span>
              </span>
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu.Root>
    </div>
  );
}
